import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import PageHeaderStowagePlanListItem from './PageHeaderStowagePlanListItem';
import { ReactNode, useState } from 'react';
import { Container, PaginationContainer } from './PageHeaderStowagePlanListGroup.styles';
import { TextButton } from '../../../styles/button.style';

interface StowagePlanListGroupProps {
    stowagePlans: StowagePlan[];
    onClick?: (ev: any, stowagePlan: StowagePlan) => void;
    selectedPlan?: StowagePlan;
}

const ITEMS_PER_PAGE = 10;
const MAX_VISIBLE_PAGES = 5;

const PageHeaderStowagePlanListGroup = ({ stowagePlans, onClick }: StowagePlanListGroupProps): ReactNode => {
    const [currentPage, setCurrentPage] = useState(1);
    const totalPages = Math.ceil(stowagePlans.length / ITEMS_PER_PAGE);

    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const visibleStowagePlans = stowagePlans.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    const handlePageChange = (pageNumber: number): void => {
        setCurrentPage(pageNumber);
    };

    // Generate the array of page numbers to display
    const getPageNumbers = (): (number | string)[] => {
        // If we have fewer pages than the max visible, show all pages
        if (totalPages <= MAX_VISIBLE_PAGES) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        // Always include first and last page
        const pageNumbers: (number | string)[] = [];

        // Always show page 1
        pageNumbers.push(1);

        // Logic for middle pages with ellipsis
        if (currentPage <= 3) {
            // Near the start
            pageNumbers.push(2, 3, 4, '...');
        } else if (currentPage >= totalPages - 2) {
            // Near the end
            pageNumbers.push('...', totalPages - 3, totalPages - 2, totalPages - 1);
        } else {
            // Middle - show ellipsis on both sides
            pageNumbers.push('...', currentPage - 1, currentPage, currentPage + 1, '...');
        }

        // Always show the last page if not already included
        if (!pageNumbers.includes(totalPages)) {
            pageNumbers.push(totalPages);
        }

        return pageNumbers;
    };

    return (
        <Container>
            <div>
                {visibleStowagePlans.map((stowagePlan) => (
                    <PageHeaderStowagePlanListItem
                        key={'stowagePlanListIten-' + stowagePlan.stowagePlanGuid}
                        stowagePlan={stowagePlan}
                        onClick={(e) => onClick?.(e, stowagePlan)}
                    />
                ))}
            </div>

            {totalPages > 1 && (
                <PaginationContainer>
                    <TextButton onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                        Previous
                    </TextButton>

                    {getPageNumbers().map((pageNumber, index) =>
                        typeof pageNumber === 'number' ? (
                            <TextButton
                                key={`page-${pageNumber}`}
                                onClick={() => handlePageChange(pageNumber)}
                                isSelected={currentPage === pageNumber}
                            >
                                {pageNumber}
                            </TextButton>
                        ) : (
                            <span key={`ellipsis-${index}`}>...</span>
                        )
                    )}

                    <TextButton onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                        Next
                    </TextButton>
                </PaginationContainer>
            )}
        </Container>
    );
};

export default PageHeaderStowagePlanListGroup;
